/**
 * Pendientes de firma — la pantalla donde el trabajador firma.
 *
 * Sirve a los dos contextos, igual que el resto del portal: `/colaboradores/…`
 * (Caso A, cuenta propia) y `/portal/…` (Caso B, entrando desde el ERP). El
 * cliente HTTP lo resuelve `usePortalClient` por la ruta, así que acá no hay
 * nada que decidir.
 *
 * 🔴 **El orden importa: primero leer, después firmar.** El documento se abre
 * antes de pedir el código, y el botón de firmar solo aparece con el código en
 * mano. Firmar algo que no se leyó es exactamente lo que una firma no debería
 * permitir, y el diseño lo hace incómodo a propósito.
 *
 * ⚠️ Ninguna regla dura se replica acá — intentos, expiración y tope de
 * reenvíos son del backend. La pantalla solo **muestra** lo que el backend
 * informa (`attempts_left`, `resends_left`): duplicar la regla la haría
 * divergir el día que cambie una de las dos.
 */
import { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { AlertTriangle, Check, FileSignature, Loader2, X } from 'lucide-react'

import apiClient from '@/lib/apiClient'
import { portalApiClient } from '@/store/portalAuthStore'
import { EmptyState } from '@/components/ds/EmptyState'
import { Skeleton } from '@/components/ds/Skeleton'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { StatusBadge, SIGNATURE_STATUS } from '@/components/ds/StatusBadge'
import { PaginaPortal } from '../components/PaginaPortal'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import {
  usePortalSignatures,
  useRequestSignatureCode,
  useSignDocument,
  useRejectSignature,
  type SignatureRequestItem,
} from '../hooks/useEmployeePortal'

function fecha(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Mensaje de error del backend, que ya viene redactado para la persona. */
function mensajeDeError(e: unknown, porDefecto: string) {
  const data = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data
  return data?.error?.message || porDefecto
}

export default function MySignaturesPage() {
  const { data, isLoading, error } = usePortalSignatures()
  const [abierto, setAbierto] = useState<SignatureRequestItem | null>(null)
  const [params, setParams] = useSearchParams()

  // El correo trae un enlace directo (`?firmar=<id>`): con la lista ya cargada
  // se abre ese pedido solo. ⚠️ El parámetro se limpia enseguida, o cerrar el
  // modal y recargar lo volvería a abrir para siempre.
  const aFirmar = params.get('firmar')
  useEffect(() => {
    if (!aFirmar || !data?.results) return
    const pedido = data.results.find(p => p.id === aFirmar)
    if (pedido) setAbierto(pedido)
    setParams(anteriores => {
      const copia = new URLSearchParams(anteriores)
      copia.delete('firmar')
      return copia
    }, { replace: true })
  }, [aFirmar, data?.results, setParams])

  const pendientes = data?.results ?? []

  return (
    <PaginaPortal
      titulo="Pendientes de firma"
      bajada="Documentos que la empresa envió para firmar. Revisar el contenido antes de firmar."
    >
      {isLoading ? (
        <GlassPanel><Skeleton variant="block" /></GlassPanel>
      ) : error ? (
        <EmptyState variant="error" title="No se pudieron cargar los documentos" />
      ) : pendientes.length === 0 ? (
        <EmptyState
          icon={<Check size={28} aria-hidden="true" />}
          title="No hay documentos pendientes"
          description="Cuando la empresa envíe un documento para firmar, va a aparecer acá."
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {pendientes.map(p => (
            <GlassPanel
              key={p.id}
              as="li"
              className="flex flex-wrap items-center gap-4"
            >
              <FileSignature size={20} className="text-warning-content shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-content truncate">{p.document.name}</p>
                <p className="text-xs text-content-muted mt-0.5">
                  {p.document.type ? `${p.document.type} · ` : ''}Enviado el {fecha(p.requested_at)}
                </p>
              </div>
              <StatusBadge status={p.status} catalog={SIGNATURE_STATUS} />
              <button
                type="button"
                onClick={() => setAbierto(p)}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-on-primary hover:bg-primary-hover shrink-0"
              >
                Revisar y firmar
              </button>
            </GlassPanel>
          ))}
        </ul>
      )}

      {abierto && (
        <ModalDeFirma pedido={abierto} onClose={() => setAbierto(null)} />
      )}
    </PaginaPortal>
  )
}

/** El diálogo: leer el documento, pedir el código, firmar o rechazar. */
function ModalDeFirma({ pedido, onClose }: { pedido: SignatureRequestItem; onClose: () => void }) {
  const { pathname } = useLocation()
  const client = pathname.startsWith('/colaboradores') ? portalApiClient : apiClient

  const [urlDoc, setUrlDoc] = useState('')
  const [codigoPedido, setCodigoPedido] = useState(false)
  const [codigo, setCodigo] = useState('')
  const [comentarios, setComentarios] = useState('')
  const [rechazando, setRechazando] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [aviso, setAviso] = useState('')
  const [abriendo, setAbriendo] = useState(false)

  const pedirCodigo = useRequestSignatureCode()
  const firmar = useSignDocument()
  const rechazar = useRejectSignature()

  /**
   * El documento se muestra ACÁ, no en otra pestaña.
   *
   * 🔴 Antes se abría con `window.open` y eso rompía el flujo en dos: la
   * persona perdía de vista el modal, y en el móvil directamente cambiaba de
   * app. Firmar exige tener el documento delante, así que el visor va embebido
   * y se carga solo al abrir.
   */
  useEffect(() => {
    let vivo = true
    let creada = ''
    ;(async () => {
      setAbriendo(true)
      setAviso('')
      try {
        const res = await client.get(
          `/employee/documents/${pedido.document.id}/download/`,
          { responseType: 'blob', params: { inline: 1 } })
        let url: string
        if (res.data.type === 'application/json') {
          url = JSON.parse(await res.data.text()).url
        } else {
          // ⚠️ En dev el backend sirve el binario, y el blob llega sin tipo o
          // con uno genérico: sin `application/pdf` el navegador lo descarga en
          // vez de mostrarlo dentro del iframe.
          creada = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
          url = creada
        }
        if (vivo) setUrlDoc(url)
      } catch {
        if (vivo) setAviso('No se pudo cargar el documento. Intentar recargar la página.')
      } finally {
        if (vivo) setAbriendo(false)
      }
    })()
    return () => {
      vivo = false
      // Sin esto el blob queda en memoria mientras dure la pestaña.
      if (creada) URL.revokeObjectURL(creada)
    }
  }, [client, pedido.document.id])

  const onPedirCodigo = async (resend = false) => {
    setAviso('')
    try {
      await pedirCodigo.mutateAsync({ id: pedido.id, resend })
      setCodigoPedido(true)
    } catch (e) {
      setAviso(mensajeDeError(e, 'No se pudo enviar el código.'))
    }
  }

  const onFirmar = async () => {
    setAviso('')
    try {
      await firmar.mutateAsync({ id: pedido.id, code: codigo.trim(), comments: comentarios })
      onClose()
    } catch (e) {
      setAviso(mensajeDeError(e, 'No se pudo firmar.'))
      setCodigo('')
    }
  }

  const onRechazar = async () => {
    setAviso('')
    try {
      await rechazar.mutateAsync({ id: pedido.id, reason: motivo.trim(), comments: comentarios })
      onClose()
    } catch (e) {
      setAviso(mensajeDeError(e, 'No se pudo registrar el rechazo.'))
    }
  }

  const ocupado = firmar.isPending || rechazar.isPending || pedirCodigo.isPending

  /* Los botones van en el `footer` del Modal, que es fijo: quedan siempre a la
     vista mientras el documento se recorre arriba. */
  const acciones = (
    <div className="flex justify-between items-center gap-2 w-full">
      {!rechazando ? (
        <button
          type="button"
          onClick={() => setRechazando(true)}
          disabled={ocupado}
          className="px-3 py-2 text-sm rounded-lg text-danger-content-soft hover:bg-hover flex items-center gap-1.5"
        >
          <X size={14} aria-hidden="true" /> No firmar
        </button>
      ) : (
        <button
          type="button"
          onClick={() => { setRechazando(false); setMotivo(''); setAviso('') }}
          className="px-3 py-2 text-sm rounded-lg text-content-muted hover:bg-hover"
        >
          Volver
        </button>
      )}

      <div className="flex gap-2">
        <ModalCancelButton onClick={onClose} />
        {!rechazando ? (
          <button
            type="button"
            onClick={onFirmar}
            disabled={codigo.length < 6 || ocupado}
            className="px-4 py-2 text-sm rounded-lg bg-primary text-on-primary hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
          >
            {firmar.isPending
              ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              : <Check size={14} aria-hidden="true" />}
            Firmar
          </button>
        ) : (
          <button
            type="button"
            onClick={onRechazar}
            disabled={!motivo.trim() || ocupado}
            className="px-4 py-2 text-sm rounded-lg bg-danger-fill text-white hover:opacity-90 disabled:opacity-50"
          >
            Rechazar el documento
          </button>
        )}
      </div>
    </div>
  )

  return (
    <Modal
      open
      onClose={onClose}
      title={`Firmar: ${pedido.document.name}`}
      size="2xl"
      actions={acciones}
    >
      <div className="space-y-4">
        {/* El documento, a la vista y ocupando el grueso del modal. Firmar algo
            que no se leyó es lo que una firma no debería permitir, así que no
            se esconde detrás de un botón. */}
        <div className="rounded-lg border border-divider overflow-hidden bg-canvas">
          {abriendo ? (
            <div className="h-[58vh] flex items-center justify-center text-content-muted">
              <Loader2 size={20} className="animate-spin" aria-hidden="true" />
            </div>
          ) : urlDoc ? (
            <iframe
              src={urlDoc}
              title={`Documento: ${pedido.document.name}`}
              className="w-full h-[58vh] bg-white"
            />
          ) : (
            <div className="h-[58vh] flex items-center justify-center text-sm text-content-muted px-6 text-center">
              No se pudo mostrar el documento.
            </div>
          )}
        </div>

        {/* ⚠️ La salida de escape: varios navegadores móviles no renderizan un
            PDF dentro de un iframe, y sin esto la persona se queda sin leer. */}
        {urlDoc && (
          <a
            href={urlDoc}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-content-muted hover:text-content underline"
          >
            Abrir el documento en otra pestaña
          </a>
        )}

        {!rechazando ? (
          <div className="grid gap-4 md:grid-cols-2 items-start">
            <div>
              {!codigoPedido ? (
                <>
                  <button
                    type="button"
                    onClick={() => onPedirCodigo(false)}
                    disabled={!urlDoc || ocupado}
                    title={!urlDoc ? 'Esperar a que cargue el documento' : undefined}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
                  >
                    Enviarme el código de verificación
                  </button>
                  <p className="text-xs text-content-muted mt-1.5">
                    Llega a <strong>{pedido.email_target}</strong>.
                  </p>
                </>
              ) : (
                <div className="space-y-2">
                  <label className="text-xs text-content-muted" htmlFor="firma-codigo">
                    Código enviado a <strong>{pedido.email_target}</strong>
                  </label>
                  <input
                    id="firma-codigo"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={codigo}
                    onChange={e => setCodigo(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="w-full px-3 py-2 rounded-lg bg-input-bg border border-input-border text-center text-lg tracking-[0.4em] font-mono focus-visible:ring-2 focus-visible:ring-focus-ring"
                  />
                  <button
                    type="button"
                    onClick={() => onPedirCodigo(true)}
                    disabled={ocupado || pedido.resends_left === 0}
                    className="text-xs text-content-muted hover:text-content underline disabled:no-underline disabled:opacity-50"
                  >
                    {pedido.resends_left === 0
                      ? 'Sin reenvíos disponibles'
                      : `Reenviar el código (quedan ${pedido.resends_left})`}
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-content-muted" htmlFor="firma-comentarios">
                Comentarios (opcional)
              </label>
              <textarea
                id="firma-comentarios"
                rows={3}
                value={comentarios}
                onChange={e => setComentarios(e.target.value)}
                placeholder="Quedan registrados junto con la firma."
                className="w-full mt-1 px-3 py-2 text-sm rounded-lg bg-input-bg border border-input-border"
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="text-xs text-content-muted" htmlFor="firma-motivo">
              Motivo del rechazo (obligatorio)
            </label>
            <textarea
              id="firma-motivo"
              rows={3}
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Explicar por qué no se firma. Lo recibe quien envió el documento."
              className="w-full mt-1 px-3 py-2 text-sm rounded-lg bg-input-bg border border-input-border"
            />
          </div>
        )}

        {aviso && (
          <p className="text-sm text-danger-content-soft flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            {aviso}
          </p>
        )}

        {/* La aclaración legal va en la pantalla y no solo en la constancia: es
            donde la persona decide. */}
        <p className="text-xs text-content-muted border-t border-divider pt-3">
          Firma electrónica <strong>simple</strong> (Ley 19.799). Quedan registrados la fecha,
          la hora y el documento firmado.
        </p>
      </div>
    </Modal>
  )
}
